import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CaretDown, CaretUp, CalendarBlank, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);

export function PeriodReportCard() {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), 0, 1)));
  const [to, setTo] = useState(iso(now));
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!from || !to) { toast.error("Pick both dates"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/reports/summary?date_from=${from}&date_to=${to}`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open && !report) run(); }, [open]); // eslint-disable-line

  const Tile = ({ label, value, color, sub, testid }) => (
    <div className="rounded-xl border border-[#C4C8CE] bg-white p-3">
      <div className="text-xs uppercase tracking-[0.1em] text-[#000000]">{label}</div>
      <div className="font-display font-extrabold text-xl" style={color ? { color } : undefined} data-testid={testid}>{value}</div>
      {sub && <div className="text-[10px] text-[#000000]">{sub}</div>}
    </div>
  );

  return (
    <div className="card" data-testid="period-report-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="period-report-toggle">
        <div className="flex items-center gap-2">
          <CalendarBlank size={22} weight="duotone" color="#B45309" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Summary report (pick a period)</h3>
            <p className="text-xs text-[#000000]">Sales, payments received, outstanding, and GST/PST/HST owed vs collected for any from–to window</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">From
              <input data-testid="report-from" className="input-base" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">To
              <input data-testid="report-to" className="input-base" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button type="button" className="btn-primary text-sm" onClick={run} disabled={busy} data-testid="report-run">{busy ? "Running…" : "Run report"}</button>
            <button type="button" className="btn-secondary text-xs" data-testid="report-this-year"
              onClick={() => { setFrom(iso(new Date(new Date().getFullYear(), 0, 1))); setTo(iso(new Date())); }}>This year</button>
            <button type="button" className="btn-secondary text-xs" data-testid="report-last-year"
              onClick={() => { const y = new Date().getFullYear() - 1; setFrom(`${y}-01-01`); setTo(`${y}-12-31`); }}>Last year</button>
          </div>

          {report && (
            <>
              <div className="text-xs text-[#000000]">
                {report.invoice_count} invoice{report.invoice_count === 1 ? "" : "s"} issued {report.date_from} → {report.date_to}
              </div>
              <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Tile label="Sales (invoiced)" value={fmt(report.sales_cents)} testid="report-sales" />
                <Tile label="Payments received" value={fmt(report.collected_cents)} color="#0A6B2C" sub="All payments dated in the window" testid="report-collected" />
                <Tile label="Outstanding" value={fmt(report.outstanding_cents)} color="#92400E" sub="Still unpaid on window invoices" testid="report-outstanding" />
                <Tile label="Tax owed to gov't" value={fmt(report.tax_invoiced_cents)} color="#B91C1C" sub="Tax on invoices issued (accrual basis)" testid="report-tax-invoiced" />
                <Tile label="Tax collected" value={fmt(report.tax_collected_cents)} sub="Portion of that tax actually received" testid="report-tax-collected" />
              </div>
              {Object.keys(report.tax_by_type || {}).length > 0 && (
                <table className="w-full text-sm max-w-md" data-testid="report-tax-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#000000] border-b border-[#C4C8CE]">
                      <th className="px-2 py-1">Tax type</th>
                      <th className="px-2 py-1 text-right">Invoiced (owed)</th>
                      <th className="px-2 py-1 text-right">Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.tax_by_type).map(([name, t]) => (
                      <tr key={name} className="border-b border-[#ECEDEF]">
                        <td className="px-2 py-1 font-bold">{name}</td>
                        <td className="px-2 py-1 text-right">{fmt(t.invoiced_cents)}</td>
                        <td className="px-2 py-1 text-right">{fmt(t.collected_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {report.stripe_sale_count > 0 && (
                <div className="text-xs text-[#000000]" data-testid="report-stripe-line">
                  Plus <b>{report.stripe_sale_count}</b> Stripe card sale{report.stripe_sale_count === 1 ? "" : "s"} totalling <b>{fmt(report.stripe_sales_cents)}</b> in this window (tax handled by Stripe Tax).
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function TaxMatrixCard() {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    if (open && !rates) {
      api.get("/admin/tax-rates").then((r) => setRates(r.data)).catch((e) => toast.error(formatApiError(e)));
    }
  }, [open]); // eslint-disable-line

  return (
    <div className="card" data-testid="tax-matrix-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="tax-matrix-toggle">
        <div className="flex items-center gap-2">
          <Scales size={22} weight="duotone" color="#0E7490" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Tax matrix (Canada)</h3>
            <p className="text-xs text-[#000000]">GST/PST/HST per province — the Tax Province selector on each document applies these rates</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && rates && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tax-matrix-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#000000] border-b border-[#C4C8CE]">
                <th className="px-2 py-1">Province / territory</th>
                <th className="px-2 py-1">Taxes applied</th>
                <th className="px-2 py-1 text-right">Total rate</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((p) => (
                <tr key={p.code} className="border-b border-[#ECEDEF]" data-testid={`tax-row-${p.code}`}>
                  <td className="px-2 py-1"><b>{p.label}</b> <span className="text-xs text-[#000000]">({p.code})</span></td>
                  <td className="px-2 py-1">{p.lines.map((l) => `${l.name} ${(l.rate * 100).toLocaleString("en-CA", { maximumFractionDigits: 3 })}%`).join(" + ")}</td>
                  <td className="px-2 py-1 text-right font-bold">{(p.total_rate * 100).toLocaleString("en-CA", { maximumFractionDigits: 3 })}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
